import { c as _c } from "react/compiler-runtime";
import { MeasurementSourceBadge } from "@/components/patient/measurements/measurement-source-badge";
import { cx } from "@/lib/tailwind-utils";
import type { MeasurementField, ReferenceRange } from "@/types/measurement";
export interface MeasurementChartPayloadItem {
  dataKey?: unknown;
  name?: unknown;
  value?: unknown;
  color?: string;
  payload?: Record<string, unknown>;
}
export interface MeasurementChartPointSource {
  isUserReported?: boolean | null;
}
interface MeasurementChartTooltipProps {
  active?: boolean;
  payload?: MeasurementChartPayloadItem[];
  label?: string;
  /** For multi-field charts: maps field ID to field metadata */
  fieldMap?: Map<string, MeasurementField>;
  /** For single-field charts: the field display name */
  fieldName?: string;
  /** For single-field charts: the unit string */
  unit?: string | null;
  /** Optional reference ranges to match values against (shows matching range label) */
  referenceRanges?: ReferenceRange[];
  /** "sm" for patient view, "xs" for settings preview */
  size?: "sm" | "xs";
  /** Resolve per-point source metadata for the hovered series. */
  getPointSource?: (item: MeasurementChartPayloadItem) => MeasurementChartPointSource | null | undefined;
}
export default function MeasurementChartTooltip(t0) {
  const $ = _c(34);
  const {
    active,
    payload,
    label,
    fieldMap,
    fieldName,
    unit,
    referenceRanges,
    size: t1,
    getPointSource
  } = t0;
  const size = t1 === undefined ? "sm" : t1;
  if (!active || !payload?.length) {
    return null;
  }
  const isSmall = size === "xs";
  const outerClass = isSmall ? "rounded-md border border-border bg-background/80 text-caption shadow-md backdrop-blur-2xl" : "rounded-md border border-border bg-background/80 text-body shadow-md backdrop-blur-2xl";
  const headerPadding = isSmall ? "px-3 py-1.5" : "px-4 py-2";
  const bodyPadding = isSmall ? "px-3 py-1.5" : "px-4 py-2";
  const bodySpacing = isSmall ? "space-y-0.5" : "space-y-1";
  const swatchClass = isSmall ? "h-[3px] w-3 shrink-0 rounded-full" : "h-[3px] w-3.5 shrink-0 rounded-full";
  const gapClass = isSmall ? "gap-6" : "space-x-8";
  let t2;
  if ($[0] !== headerPadding) {
    t2 = cx("border-b border-inherit", headerPadding);
    $[0] = headerPadding;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <p className="font-medium text-foreground">{label}</p>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== bodyPadding || $[8] !== bodySpacing) {
    t5 = cx(bodySpacing, bodyPadding);
    $[7] = bodyPadding;
    $[8] = bodySpacing;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== fieldMap || $[11] !== fieldName || $[12] !== gapClass || $[13] !== getPointSource || $[14] !== payload || $[15] !== referenceRanges || $[16] !== swatchClass || $[17] !== unit) {
    let t7;
    if ($[19] !== fieldMap || $[20] !== fieldName || $[21] !== gapClass || $[22] !== getPointSource || $[23] !== referenceRanges || $[24] !== swatchClass || $[25] !== unit) {
      t7 = (item, i) => {
        const field = fieldMap?.get(String(item.dataKey));
        const displayName = field?.name ?? fieldName ?? String(item.name ?? item.dataKey ?? "");
        const displayUnit = field?.unit ?? unit;
        const valueStr = displayUnit ? `${String(item.value)} ${displayUnit}` : String(item.value);
        const isUserReported = Boolean(getPointSource?.(item)?.isUserReported);
        const numericValue = typeof item.value === "number" ? item.value : null;
        const matchedRange = numericValue != null ? referenceRanges?.find(r => {
          const aboveMin = r.min == null || numericValue >= r.min;
          const belowMax = r.max == null || numericValue < r.max;
          return aboveMin && belowMax;
        }) : undefined;
        return <div key={i}><div className={cx("flex items-center justify-between", gapClass)}><div className="flex items-center space-x-2"><span className={swatchClass} style={{
                backgroundColor: item.color
              }} /><p className="whitespace-nowrap text-foreground-secondary">{displayName}</p></div><p className="whitespace-nowrap text-right font-medium tabular-nums text-foreground">{valueStr}</p></div>{(isUserReported || matchedRange) && <div className="mt-1 flex flex-wrap items-center gap-1.5"><MeasurementSourceBadge isUserReported={isUserReported} compact={true} />{matchedRange && <p className="text-caption-strong" style={{
              color: matchedRange.color
            }}>{matchedRange.label}{" "}{matchedRange.min != null && matchedRange.max != null ? <span>{`[${matchedRange.min}–${matchedRange.max})`}</span> : <span>{matchedRange.min != null ? `(≥${matchedRange.min})` : matchedRange.max != null ? `(<${matchedRange.max})` : null}</span>}</p>}</div>}</div>;
      };
      $[19] = fieldMap;
      $[20] = fieldName;
      $[21] = gapClass;
      $[22] = getPointSource;
      $[23] = referenceRanges;
      $[24] = swatchClass;
      $[25] = unit;
      $[26] = t7;
    } else {
      t7 = $[26];
    }
    t6 = payload.map(t7);
    $[10] = fieldMap;
    $[11] = fieldName;
    $[12] = gapClass;
    $[13] = getPointSource;
    $[14] = payload;
    $[15] = referenceRanges;
    $[16] = swatchClass;
    $[17] = unit;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[27] !== t5 || $[28] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== outerClass || $[31] !== t4 || $[32] !== t7) {
    t8 = <div className={outerClass}>{t4}{t7}</div>;
    $[30] = outerClass;
    $[31] = t4;
    $[32] = t7;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  return t8;
}